import type { BodyId, BodyState, GraphicsProfile, RenderMode, ScaleMode } from '../core/types';
import {
  BODY_IDS,
  DEFAULT_CENTRAL_BODY,
  DEFAULT_SELECTED_BODY,
  getBody
} from '../core/reference-data';
import { DEFAULT_SECTOR_COUNT } from '../core/sectors';
import { DEFAULT_VIEW_CAMERA, type ViewCamera } from './camera';

/**
 * Default playback speed, in simulated days per real second (converted to dt in
 * the state ticker). Playback speed is state, not body reference data: it lives
 * in `state` so `core` never sees sim-speed (AD-2) and holds no day literal
 * (AD-5).
 */
export const DEFAULT_SPEED_DAYS_PER_SECOND = 10;

/**
 * The single application state shape (AD-2). Holds all simulation and UI state.
 * Render and UI subscribe read-only; state changes only via a dispatched command.
 *
 * `centralBody` and `selectedBody` are distinct fields even when they name the
 * same body: the central body shows central treatment, not the edit-selection
 * ring (Consistency Conventions).
 */
export interface AppState {
  /** The nine bodies with their current lifecycle status (never deleted, AD-2). */
  bodies: BodyState[];
  /** The current central body (default: sun). */
  centralBody: BodyId;
  /** The current selected (edit-focus) body (default: earth). */
  selectedBody: BodyId;
  /** Whether the simulation is paused. Story 1.1 is a static, paused view. */
  paused: boolean;
  /** Simulation time in SI seconds. Never read from Date in core or reducers. */
  time: number;
  /** Playback speed in simulated days per real second (converted to dt in the ticker). */
  speedDaysPerSecond: number;
  /** Legend display order (canonical order by default). */
  legendOrder: BodyId[];
  /**
   * Session-only dragged-phase offset per body, in mean-anomaly RADIANS (Story
   * 1.7, FR-19). Effective mean anomaly = base(n*t) + offset, so a drag/step
   * shifts phase WITHOUT advancing `time`. Seeded to 0 for every body; `Reset`
   * (via this factory) clears it. Excluded from the shareable-config set (AD-4
   * excludes "instantaneous and dragged phase"); it is never serialized. It lives
   * in `state`, never on the pure `core` `Body`/`BodyState` (AD-1/AD-5).
   */
  phaseOffsets: Record<BodyId, number>;
  /**
   * Requested graphics profile (Story 1.6). A device-local preference (AD-7),
   * default `'auto'`. Graphics profile and reduced-effects are INDEPENDENT axes.
   */
  requestedProfile: GraphicsProfile;
  /**
   * In-app reduced-effects preference (Story 1.6). Device-local (AD-7), default
   * `false`. Effective reduced-motion is the union of this and the OS preference.
   */
  reducedEffects: boolean;
  /**
   * The concrete render mode actually mounted (Story 1.6). Runtime-derived from
   * capabilities plus the requested profile; the composition root / canvas host
   * overwrites the default once resolution runs. Never persisted (device-specific).
   */
  activeRenderMode: RenderMode;
  /**
   * Enhanced decoration features unavailable on this device (Story 1.6). Set with
   * `activeRenderMode`; readable diagnostics text (AC 5, 7). Never persisted.
   */
  unavailableFeatures: readonly string[];
  /**
   * Whether the equal-time swept-area overlay is shown for the selected body's
   * orbit (Story 2.1, FR-9). Default `false` (the learner ENABLES it). This is
   * SHAREABLE experiment configuration (AD-4 include-set), not a device-local
   * preference, so it lives in the store and changes only via a named command
   * (`SetSectorOverlayVisible`); `Reset` restores it to `false` via this factory.
   */
  sectorOverlayVisible: boolean;
  /**
   * Number of equal-time sectors in the overlay (Story 2.1, FR-9). Default
   * `DEFAULT_SECTOR_COUNT` (6); the reducer clamps edits to [2, 24]. Shareable
   * experiment configuration (AD-4), changed only via `SetSectorCount`.
   */
  sectorCount: number;
  /**
   * Session-only VIEW camera (Story 3.2, FR-11): a zoom multiplier over the fit
   * baseline plus the world-metre viewport centre (pan). Seeded to
   * `DEFAULT_VIEW_CAMERA` here (framed to ~Jupiter so the inner planets are not
   * crushed onto the Sun at boot), so `Reset` returns to that framing; the Fit
   * button (`FitAllOrbits`) is what zooms out to every orbit. This is TRANSIENT
   * SESSION-ONLY VIEW STATE, EXCLUDED from the
   * shareable-config set (AD-4 excludes "zoom/pan"); the future share codec must
   * never serialize it. The reducer keeps it viewport-free; `composeCamera` folds
   * in the viewport at render time (AD-10).
   */
  camera: ViewCamera;
  /**
   * Body-size scale mode (Story 3.3, FR-12). Default `'readable'` (the GCSE-
   * friendly default; enlarged artwork). This is SHAREABLE experiment
   * configuration (AD-4 include-set lists "scale mode"), NOT a device-local
   * preference like the graphics fields, so it lives in the store and changes only
   * via `SetScaleMode`; `Reset` restores `'readable'` via this fresh factory
   * (exactly like `sectorOverlayVisible`), and it is NOT added to the reducer's
   * Reset preserve-list. Only the drawn marker radius reads it (AD-1/AD-10).
   */
  scaleMode: ScaleMode;
}

/**
 * Pure factory for the default system (AD-2, Reset semantics): nine bodies all
 * active, Sun centre, Earth selected, time zero, default speed, canonical legend
 * order. Returns a fresh plain object (no runes) so it is trivially unit-testable
 * headless and reused by both the store and the `Reset` reducer.
 */
export function createDefaultState(): AppState {
  // Live orbital elements are the single source of truth for each body's current
  // orbit (Story 1.3): seeded here from the reference-data defaults, then edited
  // via SetEccentricity. Reference-data a/e are seed values only from now on.
  const bodies: BodyState[] = BODY_IDS.map((id) => {
    const seed = getBody(id);
    return { id, status: 'active', a: seed.a, e: seed.e };
  });
  // Session-only dragged phase, seeded to 0 for every body (Story 1.7). Reset
  // returns this fresh factory, so it clears all dragged phase.
  const phaseOffsets = Object.fromEntries(BODY_IDS.map((id) => [id, 0])) as Record<
    BodyId,
    number
  >;
  return {
    bodies,
    centralBody: DEFAULT_CENTRAL_BODY,
    selectedBody: DEFAULT_SELECTED_BODY,
    paused: true,
    time: 0,
    speedDaysPerSecond: DEFAULT_SPEED_DAYS_PER_SECOND,
    legendOrder: [...BODY_IDS],
    phaseOffsets,
    // Graphics defaults (Story 1.6). `'auto'` + no reduced-effects; the resolved
    // concrete mode starts at the safe Canvas2D baseline until the composition
    // root resolves capabilities. These are device-local preferences, not part of
    // the simulated experiment, so `Reset` preserves them (see the reducer).
    requestedProfile: 'auto',
    reducedEffects: false,
    activeRenderMode: 'compatibility',
    unavailableFeatures: [],
    // Equal-time-sector overlay (Story 2.1). Off by default; six sectors. Part of
    // the SIMULATED experiment (shareable, AD-4), so Reset restores these via this
    // fresh factory and does NOT re-preserve them like the device-local graphics
    // fields do.
    sectorOverlayVisible: false,
    sectorCount: DEFAULT_SECTOR_COUNT,
    // Session-only view camera (Story 3.2). Seeded to the ~Jupiter framing so the
    // inner planets are not crushed onto the Sun at boot; Reset returns here and
    // the Fit button zooms out to every orbit. Excluded from share (AD-4).
    camera: DEFAULT_VIEW_CAMERA,
    // Body-scale mode (Story 3.3). Readable is the GCSE-friendly default. Part of
    // the SIMULATED experiment (shareable, AD-4), so Reset restores it via this
    // fresh factory and does NOT re-preserve it like the device-local graphics
    // fields do.
    scaleMode: 'readable'
  };
}
